import { useState } from "react";

import { updateProfileName } from "../../state/accountProfile";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";

/** Matches the `varchar(100)` name columns the server validates against. */
const NAME_MAX_LENGTH = 100;

/**
 * Edit the person's own first and last name. Opens prefilled with the current
 * name; saving writes the platform user row and refreshes the shared account
 * snapshot, so the account menu updates as the dialog closes.
 */
export function ChangeNameDialog({
  open,
  onOpenChange,
  currentFirstName,
  currentLastName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentFirstName: string;
  currentLastName: string;
}) {
  const [firstName, setFirstName] = useState(currentFirstName);
  const [lastName, setLastName] = useState(currentLastName);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = !isBusy && firstName.trim().length > 0;

  const save = async () => {
    if (!canSave) return;
    setIsBusy(true);
    setError(null);
    const failure = await updateProfileName({ firstName, lastName });
    setIsBusy(false);
    if (failure) {
      setError(failure);
      return;
    }
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && isBusy) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogPopup className="max-w-md">
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <DialogHeader>
            <DialogTitle>Change name</DialogTitle>
          </DialogHeader>
          <DialogPanel className="space-y-4">
            <p className="text-sm text-muted-foreground">
              How you appear to your team: in the account menu, the team roster and next to your
              activity.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="change-name-first">
                  First name
                </label>
                <Input
                  id="change-name-first"
                  autoFocus
                  autoComplete="given-name"
                  required
                  maxLength={NAME_MAX_LENGTH}
                  value={firstName}
                  disabled={isBusy}
                  onChange={(event) => setFirstName(event.currentTarget.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="change-name-last">
                  Last name
                </label>
                <Input
                  id="change-name-last"
                  autoComplete="family-name"
                  maxLength={NAME_MAX_LENGTH}
                  value={lastName}
                  disabled={isBusy}
                  onChange={(event) => setLastName(event.currentTarget.value)}
                />
              </div>
            </div>
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </DialogPanel>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              disabled={isBusy}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave}>
              {isBusy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}
